import React, { useEffect } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Calculator } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatBot from '../components/ChatBot';
import PricingTiers from '../components/services/PricingTiers';
import ServiceFaq from '../components/services/ServiceFaq';
import RelatedServices from '../components/services/RelatedServices';
import Testimonials from '../components/Testimonials';
import { detailedServices } from '../data/servicesData';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = detailedServices.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) document.title = `${service.title} | iWurks360Digital Solutions`;
  }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const requestQuote = (label) => navigate('/', { state: { scrollTo: 'contact', service: label } });

  return (
    <div className="min-h-screen bg-white" data-testid={`service-page-${service.slug}`}>
      <Header />
      <main>
        <section className="pt-32 sm:pt-40 pb-16 bg-gray-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 30%, #FF9900 0%, transparent 50%)' }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> All Services &amp; Pricing
            </Link>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              {service.title}
            </h1>
            <p className="text-base md:text-lg font-semibold mb-4" style={{ color: '#FF9900' }}>{service.tagline}</p>
            <p className="text-gray-300 max-w-2xl text-base md:text-lg">{service.description}</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide">What you get</h2>
            <ul className="space-y-3">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D97904' }} />
                  {d}
                </li>
              ))}
            </ul>
            <Button
              asChild
              variant="outline"
              data-testid="service-page-quote-calc-link"
              className="mt-8 w-full rounded-full border-gray-900"
            >
              <Link to={`/quote?add=${service.slug}`}>
                <Calculator className="w-4 h-4 mr-2" /> Add to Quote Calculator
              </Link>
            </Button>
          </div>
          <div className="lg:col-span-2">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide">
              Packages <span className="font-normal text-gray-500 normal-case">— 27.5% below market average</span>
            </h2>
            <PricingTiers service={service} onQuote={requestQuote} />
          </div>
        </section>

        <ServiceFaq faqs={service.faqs} />
        <Testimonials limit={3} serviceSlug={service.slug} />
        <RelatedServices currentSlug={service.slug} />
      </main>
      <Footer />
      <ChatBot />
      <Toaster position="top-right" />
    </div>
  );
}
